import { useState } from "react";
import type { AccountDto, AccountMailStatsDto } from "../../../shared/types";
import { ReauthError, useFilterCounts, useFilters, useStats } from "../../hooks/useApi";
import { providerMeta } from "../providerMeta";
import { SWATCH_CLASSES } from "../filterColors";
import { AccountMailStats } from "./MailStats";

function Avatar({ account }: { account: AccountDto }) {
  const [failed, setFailed] = useState(false);
  const initial = (account.displayName ?? account.username).charAt(0).toUpperCase();
  if (failed) {
    return (
      <div className="grid size-12 place-items-center rounded-full bg-accent text-[18px] font-semibold text-white">
        {initial}
      </div>
    );
  }
  return (
    <img
      src={`/api/accounts/${account.id}/photo`}
      alt=""
      onError={() => setFailed(true)}
      className="size-12 rounded-full object-cover"
    />
  );
}

/** Per-filter matched-message counts for this account (from cached labels). */
function FilterBreakdown({ accountId }: { accountId: number }) {
  const { data: filters } = useFilters();
  const { data: counts } = useFilterCounts(accountId);

  const enabled = (filters ?? [])
    .filter((f) => f.enabled)
    .sort((a, b) => a.order - b.order);
  if (enabled.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 border-t border-stroke pt-3">
      {enabled.map((f) => (
        <span
          key={f.id}
          title={`${f.name}: ${counts?.[f.id] ?? 0} message${(counts?.[f.id] ?? 0) === 1 ? "" : "s"}`}
          className="flex items-center gap-1 rounded-full bg-black/[.04] px-2 py-0.5 text-[11px] text-ink"
        >
          <span className={`size-2 rounded-full ${SWATCH_CLASSES[f.color]}`} />
          {f.name}
          <span className="font-semibold">{counts?.[f.id] ?? 0}</span>
        </span>
      ))}
    </div>
  );
}

export function AccountCard({
  account,
  mailStats,
  onOpen,
}: {
  account: AccountDto;
  mailStats?: AccountMailStatsDto;
  onOpen: () => void;
}) {
  const { data: stats, error } = useStats(account.id);
  const needsReauth = error instanceof ReauthError || error?.name === "ReauthError";

  return (
    <div className="flex min-h-[150px] flex-col rounded-lg border border-stroke bg-white p-4">
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <Avatar account={account} />
          <span
            className="absolute -bottom-0.5 -right-0.5 grid size-4 place-items-center rounded-full border-2 border-white text-[8px] font-bold text-white"
            style={{ backgroundColor: providerMeta(account.provider).color }}
            title={providerMeta(account.provider).label}
          >
            {providerMeta(account.provider).glyph}
          </span>
        </div>
        <div className="min-w-0">
          <p className="truncate text-[14px] font-semibold">
            {account.displayName ?? account.username}
          </p>
          <p className="truncate text-[12px] text-ink-muted">{account.username}</p>
        </div>
      </div>

      <div className="flex-1 pt-3 text-[13px]">
        {needsReauth ? (
          <p className="text-ink-muted">Session expired.</p>
        ) : error ? (
          <p className="text-red-700">{error.message}</p>
        ) : !stats ? (
          <p className="text-ink-muted">Loading…</p>
        ) : (
          <>
            <p>
              <span className="mr-1.5 inline-block min-w-6 rounded-full bg-accent px-1.5 text-center text-[11px] font-semibold leading-[18px] text-white">
                {stats.unreadCount}
              </span>
              unread of {stats.totalCount} in inbox
            </p>
            <p className="truncate pt-1.5 text-ink-muted">
              {stats.nextEvent
                ? `Next: ${stats.nextEvent.subject} · ${
                    stats.nextEvent.isAllDay
                      ? new Date(stats.nextEvent.start).toLocaleDateString(undefined, {
                          weekday: "short",
                        }) + " (all day)"
                      : new Date(stats.nextEvent.start).toLocaleString(undefined, {
                          weekday: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                  }`
                : "No events in the next 7 days"}
            </p>
          </>
        )}
      </div>

      {!needsReauth && !error && <AccountMailStats stats={mailStats} />}
      {!needsReauth && !error && <FilterBreakdown accountId={account.id} />}

      <div className="pt-3">
        {needsReauth ? (
          providerMeta(account.provider).loginUrl ? (
            <a
              href={`${providerMeta(account.provider).loginUrl}?hint=${encodeURIComponent(account.username)}`}
              className="inline-block rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover"
            >
              Reconnect
            </a>
          ) : (
            // IMAP: re-add via the "+" menu to update the app password.
            <span className="text-[12px] text-ink-muted">Re-add via ＋ to update the password</span>
          )
        ) : (
          <button
            onClick={onOpen}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] font-medium text-accent hover:bg-accent-subtle"
          >
            Open
          </button>
        )}
      </div>
    </div>
  );
}
